import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { formatTime, formatDate } from "../lib/format";
import { BellRinging, ChatCircleText, ArrowRight, Paperclip } from "@phosphor-icons/react";

/**
 * Unread-messages notifications card for advisors.
 * Lists conversations with unread > 0 and links to open them.
 */
export default function UnreadNotifications() {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConvs(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, []);

  const withUnread = convs.filter((c) => (c.unread || 0) > 0);
  const total = withUnread.reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <div className="bento" data-testid="unread-notifications">
      <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <BellRinging size={22} weight="duotone" className={total > 0 ? "text-burnt" : "text-inkMuted"} />
            {total > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-burnt text-bg text-[10px] font-mono flex items-center justify-center">
                {total > 99 ? "99+" : total}
              </span>
            )}
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">Mensajes sin leer</h2>
            <div className="label-mono">{total > 0 ? `${total} pendientes` : "Todo al día"}</div>
          </div>
        </div>
        <Link
          to="/app/chat"
          className="text-sm text-forest hover:text-burnt flex items-center gap-1"
          data-testid="notifications-open-chat"
        >
          Ir al chat <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      {loading ? (
        <div className="p-8 text-center text-inkMuted text-sm">Cargando…</div>
      ) : withUnread.length === 0 ? (
        <div className="p-10 text-center">
          <ChatCircleText size={32} weight="duotone" className="text-forest/40 mx-auto mb-3" />
          <div className="text-sm text-inkMuted">Sin mensajes pendientes. Buen trabajo 🌿</div>
        </div>
      ) : (
        <ul className="divide-y divide-hairline max-h-[360px] overflow-auto" data-testid="unread-list">
          {withUnread.map((c) => {
            const p = c.partner || c.client;
            return (
              <li key={c.id}>
                <Link
                  to="/app/chat"
                  state={{ focusConvId: c.id }}
                  className="flex items-center gap-3 px-6 py-3 hover:bg-bg transition-colors"
                  data-testid={`unread-item-${c.id}`}
                >
                  <div className="w-10 h-10 bg-forest text-bg flex items-center justify-center font-display font-bold flex-shrink-0">
                    {p?.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-sm truncate">{p?.name || "Cliente"}</div>
                      <span className="label-mono">{p?.role}</span>
                    </div>
                    <div className="label-mono mt-0.5">
                      Último: {c.last_message_at ? `${formatDate(c.last_message_at)} · ${formatTime(c.last_message_at)}` : "—"}
                    </div>
                  </div>
                  <span className="flex-shrink-0 min-w-[28px] h-7 px-2 bg-burnt text-bg text-xs font-mono font-semibold flex items-center justify-center">
                    {c.unread}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
